/**
 * The tab's transport: every call as REST, or every tool call as an MCP
 * `tools/call` (⇧M, the MCP lens's toggle). A per-viewer convenience, so it
 * lives in localStorage, read and written inside try/catch; it never holds a
 * key. An operation marked `x-mcp: false` is no tool and always goes as REST.
 */

import type { Operation, Transport, Unsub, Via } from '../contracts';

export const TRANSPORT_KEY = 'schedule-bit.playground.transport';

export const createTransport = (storage: Storage | null = null): Transport => {
  let via: Via = (() => {
    try {
      return storage?.getItem(TRANSPORT_KEY) === 'mcp' ? 'mcp' : 'rest';
    } catch {
      return 'rest';
    }
  })();
  const listeners = new Set<(v: Via) => void>();
  const transport: Transport = {
    get: () => via,
    set(v) {
      if (v === via) return;
      via = v;
      try {
        storage?.setItem(TRANSPORT_KEY, v);
      } catch {
        // Storage refused: the switch holds for this page's life.
      }
      for (const fn of [...listeners]) fn(v);
    },
    toggle() {
      transport.set(via === 'rest' ? 'mcp' : 'rest');
      return via;
    },
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
  return transport;
};

/** The road a call on `op` takes when `wanted` is asked for. */
export const viaFor = (op: Pick<Operation, 'mcp'> | undefined, wanted: Via): Via => (op?.mcp ? wanted : 'rest');
